// Audio registry — THE contract (BP-AUDIO-SYSTEM-01).
//
// Plain data module: no React, no Howler import. One entry per sound. The keys
// here are transcribed EXACTLY from Docs/audio-asset-spec.md (the human-facing
// generation worksheet) — they are never invented, renamed, or re-cased here.
//
//   filename = sound key = code reference = sprite-manifest entry.
//
// One string across all four places. Code imports these keys; the worksheet is
// generated from the same set; `audiosprite` derives sprite keys from the
// key-named WAVs. Neither side can type a key the other won't recognise.
//
// Scope:
//   'launch' — wired to a call site in this packet (ships silent until assets land).
//   'post'   — present in the contract for future-proofing; NOT wired anywhere yet.
//
// Counts (must match the worksheet): launch = 11 BGM/ambient + 18 SFX;
// post = 1 BGM + 7 SFX.

// ── BGM (individual files → public/assets/audio/bgm/) ───────────────────────
// `context` is what code passes to playBGM(key, context). loop: true. .mp3.
const BGM = [
  { key: 'biome-01-bamboo-forest', context: 'biome', scope: 'launch', moment: 'Map BGM, levels 1–7' },
  { key: 'biome-02-ocean-kingdom', context: 'biome', scope: 'launch', moment: 'Map BGM, levels 8–14' },
  { key: 'biome-03-mountain-temple', context: 'biome', scope: 'launch', moment: 'Map BGM, levels 15–21' },
  { key: 'biome-04-sunlit-desert', context: 'biome', scope: 'launch', moment: 'Map BGM, levels 22–28' },
  { key: 'biome-05-dragon-realm', context: 'biome', scope: 'launch', moment: 'Map BGM, levels 29–35' },
  { key: 'biome-06-moon-garden', context: 'biome', scope: 'launch', moment: 'Map BGM, levels 36–42' },
  { key: 'story-reading', context: 'reader', scope: 'launch', moment: 'Story reading screen' },
  { key: 'writing-practice', context: 'writing', scope: 'launch', moment: 'Writing practice screen' },
  { key: 'review-reel', context: 'review', scope: 'launch', moment: 'Review reel (swipe) screen' },
  { key: 'onboarding', context: 'onboarding', scope: 'launch', moment: 'Entire onboarding/first-launch flow' },
  // Uses the Bamboo Forest track as a fallback at launch; own track is post.
  { key: 'placement', context: 'placement', scope: 'post', moment: 'Placement/eval flow' },
];

// ── Ambient (individual file → public/assets/audio/ambient/) ────────────────
const AMBIENT = [
  { key: 'story-generating', context: 'ambient', scope: 'launch', moment: 'Looping wait during story generation' },
];

// ── SFX (bundled into one sprite → public/assets/audio/sfx/sfx-sprite.{mp3,json}) ──
// SFX resolve through the sprite manifest, so they carry NO per-file path.
// `surface` mirrors the worksheet column; `targetMs` is the generation target.
const SFX = [
  // LAUNCH (18)
  { key: 'stroke-correct', surface: 'writing', scope: 'launch', targetMs: 250, moment: 'Correct stroke completed' },
  { key: 'stroke-wrong', surface: 'writing', scope: 'launch', targetMs: 300, moment: 'Wrong stroke' },
  { key: 'character-mastered', surface: 'writing', scope: 'launch', targetMs: 600, moment: 'Character fully written' },
  { key: 'round-complete', surface: 'writing', scope: 'launch', targetMs: 1200, moment: 'RoundCompleteScreen appears' },
  { key: 'encourage', surface: 'writing', scope: 'launch', targetMs: 500, moment: 'Encouragement popup (2nd mistake)' },
  { key: 'xp-gain', surface: 'XP/progression', scope: 'launch', targetMs: 400, moment: 'XP awarded' },
  { key: 'level-up', surface: 'XP/progression', scope: 'launch', targetMs: 1200, moment: 'Level increases' },
  { key: 'story-ready', surface: 'story flow', scope: 'launch', targetMs: 700, moment: 'Story generation completes' },
  { key: 'story-complete', surface: 'story flow', scope: 'launch', targetMs: 900, moment: 'Story finished — ship sets sail' },
  { key: 'review-swipe-positive', surface: 'review reel', scope: 'launch', targetMs: 450, moment: 'Swipe right / Got It' },
  { key: 'review-swipe-neutral', surface: 'review reel', scope: 'launch', targetMs: 400, moment: 'Swipe left / Still Hard' },
  { key: 'buddy-celebration-pop', surface: 'review reel', scope: 'launch', targetMs: 500, moment: 'Buddy celebration mid-reel' },
  { key: 'reel-complete', surface: 'review reel', scope: 'launch', targetMs: 800, moment: 'Review session ends' },
  { key: 'node-tap', surface: 'map', scope: 'launch', targetMs: 200, moment: 'Tap an available map node' },
  { key: 'node-tap-locked', surface: 'map', scope: 'launch', targetMs: 250, moment: 'Tap a locked node' },
  { key: 'button-tap', surface: 'UI global', scope: 'launch', targetMs: 120, moment: 'Primary button press' },
  { key: 'drawer-whoosh', surface: 'UI global', scope: 'launch', targetMs: 300, moment: 'Drawer / overlay open or close' },
  { key: 'modal-chime', surface: 'UI global', scope: 'launch', targetMs: 350, moment: 'Modal / celebration appears' },

  // POST (7) — present in the contract, not wired to any call site.
  { key: 'biome-transition-reveal', surface: 'map', scope: 'post', targetMs: 2000, moment: 'Map zooms into a newly revealed biome' },
  { key: 'biome-arrival', surface: 'XP/progression', scope: 'post', targetMs: 2000, moment: 'Biome unlocked' },
  { key: 'figurine-step', surface: 'map', scope: 'post', targetMs: 250, moment: 'Figurine advances a node' },
  { key: 'page-turn', surface: 'story flow', scope: 'post', targetMs: 250, moment: 'Story page / panel advance' },
  { key: 'challenge-complete', surface: 'challenges', scope: 'post', targetMs: 600, moment: 'Daily challenge completed' },
  { key: 'weekly-sweep-complete', surface: 'challenges', scope: 'post', targetMs: 1200, moment: 'Weekly sweep completed' },
  { key: 'stamp-earned', surface: 'challenges', scope: 'post', targetMs: 400, moment: 'A stamp is earned' },
];

// Normalise each row into a uniform entry shape. `file` is the relative path
// under public/assets/audio/ for bgm/ambient (resolved by AudioManager); sfx
// have no per-file path (they live inside the sprite).
function normalise(rows, kind) {
  return rows.map((r) => {
    const base = {
      key: r.key,
      kind,
      scope: r.scope,
      moment: r.moment,
    };
    if (kind === 'bgm' || kind === 'ambient') {
      const folder = kind === 'bgm' ? 'bgm' : 'ambient';
      return {
        ...base,
        context: r.context,
        loop: true,
        file: `${folder}/${r.key}.mp3`,
      };
    }
    // sfx
    return {
      ...base,
      surface: r.surface,
      loop: false,
      targetMs: r.targetMs,
    };
  });
}

// The full registry, in a stable order: BGM, then ambient, then SFX.
export const AUDIO_MANIFEST = [
  ...normalise(BGM, 'bgm'),
  ...normalise(AMBIENT, 'ambient'),
  ...normalise(SFX, 'sfx'),
];

// Path (under public/assets/audio/) to the bundled SFX sprite + its manifest.
// SFX always resolve through these two files, never a per-key path.
export const SFX_SPRITE_FILE = 'sfx/sfx-sprite.mp3';
export const SFX_SPRITE_MANIFEST_FILE = 'sfx/sfx-sprite.json';

// Base public path. Components/manager resolve through here — never hand-build
// an `/assets/audio/...` string elsewhere.
export const AUDIO_BASE_PATH = '/assets/audio';

const BY_KEY = AUDIO_MANIFEST.reduce((acc, e) => {
  acc[e.key] = e;
  return acc;
}, {});

// ── Helpers ─────────────────────────────────────────────────────────────────
export function getEntry(key) {
  return BY_KEY[key] || null;
}

export function launchKeys() {
  return AUDIO_MANIFEST.filter((e) => e.scope === 'launch').map((e) => e.key);
}

export function sfxKeys() {
  return AUDIO_MANIFEST.filter((e) => e.kind === 'sfx').map((e) => e.key);
}

export function bgmKeys() {
  return AUDIO_MANIFEST.filter((e) => e.kind === 'bgm').map((e) => e.key);
}

// Convenience: full relative URL (e.g. '/assets/audio/bgm/story-reading.mp3')
// for a bgm/ambient entry. Returns null for sfx (they live in the sprite).
export function fileUrl(key) {
  const e = BY_KEY[key];
  if (!e || !e.file) return null;
  return `${AUDIO_BASE_PATH}/${e.file}`;
}
